/**
 * Fuente de verdad del contenido comercial de la landing.
 *
 * REGLA: no se inventan datos. No hay testimonios, estadísticas, cantidad de
 * ventas ni resultados garantizados. Lo que todavía no fue provisto queda como
 * PLACEHOLDER entre corchetes.
 */

export interface Stage {
  num: string
  name: string
  /** Cantidad de scripts de la etapa. */
  count: string
  desc: string
  /** Ejemplo corto de mensaje incluido en la etapa. */
  sample: string
}
export interface Benefit {
  title: string
  desc: string
}
export interface HowStep {
  num: string
  title: string
  desc: string
}
export interface AudienceItem {
  title: string
  desc: string
}
export interface Faq {
  q: string
  a: string
}

/** Nombre comercial único del producto. */
export const PRODUCT_NAME = '100 Scripts de WhatsApp para Cerrar Más Ventas'

export const PRODUCT_SUBTITLE =
  'Convertí consultas en dinero con mensajes listos para copiar y enviar'

/** Precio definitivo. Formato monetario único de la landing: siempre "$00.000". */
export const PRICE = {
  previous: '$24.990',
  current: '$12.990',
  note: 'Pesos argentinos · Pago único',
} as const

/** PLACEHOLDER — completar con la política de garantía real (si existe). */
export const GUARANTEE_PLACEHOLDER = '[COMPLETAR: garantía / política de devolución]'

/** PLACEHOLDER — completar con los medios de pago habilitados. */
export const PAYMENT_PLACEHOLDER = '[COMPLETAR: medios de pago]'

export const PRODUCT_FACTS: string[] = [
  '100 scripts',
  '6 etapas de venta',
  'Copiar y enviar',
  'Acceso inmediato',
]

/** Dolor: situaciones que se reconocen al instante. */
export const PAIN_SITUATIONS: string[] = [
  '«¿Cuánto sale?» … y no responde nunca más.',
  '«Lo pienso y te aviso.» Y no vuelve a escribir.',
  'Mandás el precio y la conversación se apaga.',
  'Te deja en visto y no sabés qué escribirle.',
  'Contestás distinto cada vez, según cómo venga el día.',
  'Querés hacer seguimiento, pero no querés sonar pesado.',
]

/** Solución: improvisar vs. tener el mensaje preparado. */
export const SOLUTION_BEFORE: string[] = [
  'Escribís desde cero en cada consulta.',
  'Tardás en responder y la persona ya compró en otro lado.',
  'Tirás el precio solo, sin contexto.',
  'Si no contesta, la conversación queda ahí.',
  'Nunca sabés cuál fue el mensaje que funcionó.',
]

export const SOLUTION_AFTER: string[] = [
  'Buscás la situación y ya tenés el mensaje escrito.',
  'Respondés en segundos, mientras la persona está atenta.',
  'Presentás el precio dentro de una propuesta.',
  'Tenés mensajes de seguimiento para retomar sin incomodar.',
  'Repetís siempre la misma secuencia, de la consulta al cierre.',
]

/** Beneficios concretos de usar los scripts. */
export const BENEFITS: Benefit[] = [
  {
    title: 'Respondés en segundos',
    desc: 'Copiás, ajustás el nombre y enviás. Sin quedarte pensando qué poner.',
  },
  {
    title: 'Dejás de improvisar',
    desc: 'Cada situación del chat tiene un mensaje preparado que la desbloquea.',
  },
  {
    title: 'Sabés qué preguntar',
    desc: 'Detectás qué necesita la persona antes de hablar de plata.',
  },
  {
    title: 'Presentás el precio sin miedo',
    desc: 'Mensajes que muestran el valor primero y el número después.',
  },
  {
    title: 'Retomás conversaciones frías',
    desc: 'Seguimientos que reactivan el chat sin sonar insistente.',
  },
  {
    title: 'Llevás la charla al cierre',
    desc: 'Mensajes directos para confirmar, cobrar y coordinar la entrega.',
  },
]

/** Las 6 etapas: 100 scripts en total. */
export const STAGES: Stage[] = [
  {
    num: '01',
    name: 'Aperturas y Primer Contacto',
    count: '20 scripts',
    desc: 'Para responder la primera consulta y que la conversación arranque.',
    sample: '«¡Hola, Sofi! Sí, tengo disponibilidad. ¿Para cuándo lo necesitás?»',
  },
  {
    num: '02',
    name: 'Cualificación y Detección',
    count: '20 scripts',
    desc: 'Preguntas para entender qué necesita y si realmente puede comprar.',
    sample: '«¿Lo estás buscando para vos o para regalar? Así te recomiendo mejor.»',
  },
  {
    num: '03',
    name: 'Presentación y Beneficios',
    count: '20 scripts',
    desc: 'Para explicar lo que vendés en función de lo que la persona te dijo.',
    sample: '«Por lo que me contás, te sirve la opción X: resuelve justo eso.»',
  },
  {
    num: '04',
    name: 'Oferta y Precio',
    count: '20 scripts',
    desc: 'Para dar el precio dentro de una propuesta y sostener objeciones.',
    sample: '«Te paso las dos opciones con lo que incluye cada una 👇»',
  },
  {
    num: '05',
    name: 'Seguimiento de Oportunidades',
    count: '10 scripts',
    desc: 'Para retomar al que dijo «lo pienso» y al que te dejó en visto.',
    sample: '«Te escribo por lo que hablamos el martes. ¿Seguís interesada?»',
  },
  {
    num: '06',
    name: 'Cierre y Confirmación',
    count: '10 scripts',
    desc: 'Para pedir la decisión, cobrar y confirmar la entrega.',
    sample: '«Te reservo el tuyo y te paso los datos para abonar. ¿Avanzamos?»',
  },
]

/** Cómo se usa mientras atendés. */
export const HOW_STEPS: HowStep[] = [
  {
    num: '01',
    title: 'Buscá la situación',
    desc: 'Están ordenados por etapa: apertura, precio, «lo pienso», cierre.',
  },
  {
    num: '02',
    title: 'Copiá el mensaje',
    desc: 'Cambiás el nombre y el producto. Nada más.',
  },
  {
    num: '03',
    title: 'Enviá y seguí',
    desc: 'Respondés en el momento y la conversación avanza a la etapa siguiente.',
  },
]

/** Para quién es. */
export const AUDIENCE: AudienceItem[] = [
  {
    title: 'Emprendedores',
    desc: 'Vendés tu producto por WhatsApp y atendés vos mismo cada consulta.',
  },
  {
    title: 'Comerciantes',
    desc: 'Te escriben todo el día por precio, stock y disponibilidad.',
  },
  {
    title: 'Profesionales',
    desc: 'Agendás consultas y presupuestos por chat.',
  },
  {
    title: 'Freelancers',
    desc: 'Cotizás proyectos y necesitás sostener la conversación hasta el sí.',
  },
  {
    title: 'Prestadores de servicios',
    desc: 'Coordinás turnos, visitas y trabajos desde el celular.',
  },
  {
    title: 'Vendedores',
    desc: 'Manejás varias conversaciones a la vez y hacés seguimiento.',
  },
]

/** Contenido incluido, dentro de la sección de oferta. */
export const OFFER_INCLUDES: string[] = [
  '100 scripts de WhatsApp listos para copiar y enviar',
  'Organizados en las 6 etapas de una conversación de venta',
  '20 aperturas, 20 de cualificación y 20 de presentación',
  '20 de oferta y precio, 10 de seguimiento y 10 de cierre',
  'Mensajes cortos, en español rioplatense, para adaptar en segundos',
  'Formato digital para usar desde el celular mientras atendés',
]

export const FAQS: Faq[] = [
  {
    q: '¿Qué recibo exactamente?',
    a: '100 mensajes escritos, organizados en 6 etapas: apertura, cualificación, presentación, oferta y precio, seguimiento y cierre. Los abrís, copiás el que necesitás y lo enviás.',
  },
  {
    q: '¿Es un ebook de teoría de ventas?',
    a: 'No. No es un curso ni un libro para leer. Son mensajes listos para usar mientras estás atendiendo una consulta.',
  },
  {
    q: '¿Sirve para cualquier rubro?',
    a: 'Los scripts están escritos para situaciones de conversación, no para un producto específico. Cambiás el nombre y lo que vendés, y el mensaje funciona igual.',
  },
  {
    q: '¿Los tengo que escribir de nuevo?',
    a: 'No. Están pensados para copiar y pegar. Solo ajustás el nombre de la persona y el detalle de lo que ofrecés.',
  },
  {
    q: '¿Los puedo usar desde el celular?',
    a: 'Sí. Se abren desde el celular, la tablet o la computadora, así los tenés a mano en la misma pantalla en la que atendés.',
  },
  {
    q: '¿Cómo lo recibo?',
    a: 'En formato digital, inmediatamente después de la compra. No se envía nada físico.',
  },
  {
    q: '¿Es un pago único? ¿Qué medios de pago hay?',
    a: `Sí, es una compra única de ${PRICE.current}, sin suscripciones ni pagos mensuales. Medios de pago: ${PAYMENT_PLACEHOLDER}.`,
  },
  {
    q: '¿Tiene garantía?',
    a: GUARANTEE_PLACEHOLDER,
  },
]
